import * as React from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { fromUnixTime } from 'date-fns';
import { InformationCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/solid';
import {
  addDaysToDayMonthYear,
  BinocularGirlIllustration,
  formatDate,
  formatDateToDayMonthYear,
  HeadphoneIcon,
  useAnalytics,
} from '@restworld/utils-common';
import {
  Alert,
  BaseModal,
  ButtonV2,
  FlexibleSlotCard,
  IconButton,
  SkeletonEmployerCardListLoading,
  Title,
  V2CustomLink,
} from '@restworld/ui-ds';
import { IndexStripeSubscriptions } from '@restworld/data-services';
import { FlexibleSlot, StripeSubscription } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';

import {
  useFlexibleSlots,
  useFlexibleSlotsQuery,
  useFlexibleSlotsLoading,
  useFlexibleSubscriptionSlots,
  useFlexibleSubscriptionSlotsQuery,
  useFlexibleSlotActivateMutation,
  flexibleSlotQueryKey,
  subscriptionSlotQueryKey,
  useFetchIsSubscriptionFeedbacksSubmittedQuery,
  useIsSubscriptionFeedbacksSubmitted,
  isSubscriptionFeedbacksSubmittedQueryKey,
  useCancelFlexibleSubscriptionMutation,
} from './flexible-slots-v2.hooks';
import SubscriptionFeedbackModal from '../SubscriptionFeedbackModal';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IFlexibleSlotsProps {}
const flexibleSlotAddCancelConfirmationModalAtom = atom<boolean>(false);
const flexibleSlotConfirmationTypeAtom = atom<'add' | 'cancel'>('cancel');
const flexibleSlotCancelFeedbackModalAtom = atom<boolean>(false);
export const selectedFlexibleSlotsDataAtom = atom<Partial<FlexibleSlot | StripeSubscription>>({});

// Type guard per distinguere FlexibleSlot da StripeSubscription
function isFlexibleSlot(v: FlexibleSlot | StripeSubscription): v is FlexibleSlot {
  return 'is_trial' in v;
}

export const FlexibleSlotsPageContent: React.FunctionComponent<IFlexibleSlotsProps> = () => {
  const queryClient = useQueryClient();
  const [isFlexibleSlotFeedbackModal, flexibleSlotFeedbackModalToggler] = useAtom(flexibleSlotCancelFeedbackModalAtom);
  const [selectedFlexibleSlotsData, setSelectedFlexibleSlotsData] = useAtom(selectedFlexibleSlotsDataAtom);

  useFlexibleSlotsQuery();
  useFlexibleSubscriptionSlotsQuery();
  useFetchIsSubscriptionFeedbacksSubmittedQuery();

  const onRefresh = React.useCallback(() => {
    if (selectedFlexibleSlotsData?.['expires_at'])
      queryClient.invalidateQueries({
        queryKey: flexibleSlotQueryKey,
      });
    else
      queryClient.invalidateQueries({
        queryKey: subscriptionSlotQueryKey,
      });
    queryClient.invalidateQueries({
      queryKey: isSubscriptionFeedbacksSubmittedQueryKey,
    });
    setSelectedFlexibleSlotsData({});
  }, [queryClient, selectedFlexibleSlotsData, setSelectedFlexibleSlotsData]);

  return (
    <>
      <FlexibleSlotsContent />
      <SlotAddCancelConfirmationModal />
      <SubscriptionFeedbackModal
        isOpen={isFlexibleSlotFeedbackModal}
        onClose={() => flexibleSlotFeedbackModalToggler(false)}
        stripeSubscriptionId={
          !selectedFlexibleSlotsData?.['expires_at'] ? (selectedFlexibleSlotsData as StripeSubscription).id : undefined
        }
        cancellingFlexibleSlotIds={
          selectedFlexibleSlotsData?.['expires_at'] ? [(selectedFlexibleSlotsData as FlexibleSlot).id] : undefined
        }
        refresh={onRefresh}
      />
    </>
  );
};

const SlotAddCancelConfirmationModal = () => {
  const [isOpen, setIsOpen] = useAtom(flexibleSlotAddCancelConfirmationModalAtom);
  const [selectedFlexibleSlotData, setSelectedFlexibleSlotsData] = useAtom(selectedFlexibleSlotsDataAtom);
  const flexibleSlotConfirmationType = useAtomValue(flexibleSlotConfirmationTypeAtom);
  const { activeSlots } = useFlexibleSlotActiveIdleLength();
  const [, flexibleSlotFeedbackModalToggler] = useAtom(flexibleSlotCancelFeedbackModalAtom);
  const { t } = useTranslation('payment-and-invoices');
  const {
    mutate: activateFlexibleSlot,
    isPending: isFlexibleSlotActivationPending,
    isSuccess: isFlexibleSlotActivationSuccessful,
  } = useFlexibleSlotActivateMutation();

  const onModalClose = React.useCallback(() => {
    setIsOpen(false);
    setSelectedFlexibleSlotsData({});
  }, [setIsOpen, setSelectedFlexibleSlotsData]);

  const {
    mutate: mutateCancelFlexibleSubscription,
    isPending: isCancelSlotPending,
    isSuccess: isCancelSlotSuccess,
  } = useCancelFlexibleSubscriptionMutation();

  const isSubscriptionFeedbacksSubmitted = useIsSubscriptionFeedbacksSubmitted();
  const shouldShowFeedbackModal = !isSubscriptionFeedbacksSubmitted;

  const onSlotCancel = React.useCallback(() => {
    const isFlexibleSlot = selectedFlexibleSlotData?.['expires_at'];

    if (shouldShowFeedbackModal) {
      flexibleSlotFeedbackModalToggler(true); // shows the feedback modal
      setIsOpen(false);
    } else
      mutateCancelFlexibleSubscription({
        createFeedback: false,
        ...(!isFlexibleSlot ? { stripeSubscriptionId: selectedFlexibleSlotData?.id as string } : {}),
        ...(isFlexibleSlot ? { flexibleSlotIds: [selectedFlexibleSlotData?.id as number] } : {}),
      });
  }, [
    selectedFlexibleSlotData,
    shouldShowFeedbackModal,
    mutateCancelFlexibleSubscription,
    flexibleSlotFeedbackModalToggler,
    setIsOpen,
  ]);

  const onSlotActivate = React.useCallback(() => {
    activateFlexibleSlot();
  }, [activateFlexibleSlot]);

  const expiresAt = React.useMemo(
    () =>
      selectedFlexibleSlotData
        ? selectedFlexibleSlotData?.['expires_at']
          ? formatDateToDayMonthYear(selectedFlexibleSlotData?.['expires_at'])
          : selectedFlexibleSlotData?.['current_period_end']
          ? formatDateToDayMonthYear(fromUnixTime(selectedFlexibleSlotData['current_period_end']).toISOString())
          : ''
        : '',
    [selectedFlexibleSlotData]
  );

  React.useEffect(() => {
    if (isFlexibleSlotActivationSuccessful || isCancelSlotSuccess) onModalClose();
  }, [isFlexibleSlotActivationSuccessful, isCancelSlotSuccess, onModalClose]);

  const footerEl = (
    <div tw="flex flex-col justify-center items-center gap-5 w-full pt-[64px] lg:(flex-row justify-between)">
      <ButtonV2
        onClick={flexibleSlotConfirmationType === 'add' ? onSlotActivate : onSlotCancel}
        fullwidth
        loading={isFlexibleSlotActivationPending || isCancelSlotPending}
        EndIcon={shouldShowFeedbackModal ? undefined : TrashIcon}
        variant={shouldShowFeedbackModal ? 'primary' : 'secondary-danger'}
      >
        {flexibleSlotConfirmationType === 'add'
          ? t('activate_flexible_slot')
          : shouldShowFeedbackModal
          ? t('review_and_cancel')
          : t('cancel_slot_key')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" fullwidth onClick={() => setIsOpen(false)}>
        {t('do_not_cancel')}
      </ButtonV2>
    </div>
  );

  const titleEl = (
    <Title
      content={
        flexibleSlotConfirmationType === 'add'
          ? t('activate_flexible_slot')
          : t('do_you_really_want_to_cancel_the_slot')
      }
      fontSize="extraLarge"
      fontWeight="semibold"
      textColor="neutral-700"
      twStyle={tw`text-center leading-[30px] pb-6`}
    />
  );

  return (
    <BaseModal
      showMobileGestures
      modalStyles={[tw`w-full max-w-[640px] lg:(max-w-screen-sm)`]}
      open={isOpen}
      onClose={() => onModalClose()}
      title={titleEl}
      footer={footerEl}
    >
      <div tw="flex flex-col justify-center items-center gap-5 w-full text-center">
        {flexibleSlotConfirmationType === 'add' ? (
          <Title
            content={t('new_flexible_slot_will_be_activated', { activeSlots: activeSlots + 1 })}
            fontSize="base"
            fontWeight="medium"
            textColor="neutral-700"
          />
        ) : (
          <>
            <Title
              content={t('by_proceeding_the_slot_will_be_deactivated_starting_date', {
                expiresAt: addDaysToDayMonthYear(expiresAt),
              })}
              fontSize="base"
              fontWeight="medium"
              textColor="neutral-700"
            />
            <Alert
              variant="restworld"
              text={t('cancel_slot_alert', {
                expiresAt: addDaysToDayMonthYear(expiresAt),
                count: activeSlots - 1,
              })}
              icon={<ExclamationTriangleIcon className="h-4 w-4 min-h-[16px] min-w-[16px]" />}
              wrapperStyling={tw`justify-start text-left mt-3`}
            />
            {/* <Title
              content={t('available_job_posts', {
                activeSlots: activeSlots - 1 === 0 ? 'no' : activeSlots - 1,
                expiresAt,
              })}
              fontSize="base"
              fontWeight="medium"
              textColor="neutral-700"
            /> */}
          </>
        )}
      </div>
    </BaseModal>
  );
};

const FlexibleSlotsContent = () => {
  const isFlexibleSlotsLoading = useFlexibleSlotsLoading();

  const { noData } = useFlexibleSlotActiveIdleLength();
  return isFlexibleSlotsLoading ? (
    <SkeletonEmployerCardListLoading />
  ) : (
    <div tw="flex-col justify-start items-center gap-4 inline-flex w-full">
      <SlotHeader />
      <ContentWrapper>
        <SlotSummary />
        {noData ? (
          <div tw="flex justify-center items-center w-full min-h-[350px]">
            <BinocularGirlIllustration />
          </div>
        ) : (
          <>
            <ActiveSlotExpiringSoonWarning />
            <div tw="self-stretch flex-col justify-start items-start gap-5 flex">
              <SlotCards />
              <IdleSlots />
            </div>
          </>
        )}
        <ActionButtons />
      </ContentWrapper>
    </div>
  );
};

const SlotHeader = () => {
  const { t } = useTranslation();
  const { startTour } = useIntercom();
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();

  return (
    <HeaderWrapper>
      <TitleWrapper>
        <Title
          fontSize="large"
          fontWeight="semibold"
          content={t('payment-and-invoices:flexible_slot_title')}
          textColor="neutral-700"
        />
        <IconButton
          Icon={InformationCircleIcon}
          ariaLabel="flexible-slots"
          onClick={() => startTour(599327)}
          tw="hidden lg:block" // Intercom product tours don't work from mobile
        />
      </TitleWrapper>
      {/* <Tooltip animation="duration-200" content={t('common:coming_soon')} placement="auto" trigger="hover"> */}
      <V2CustomLink
        href="/employer/purchase"
        newTab={false}
        StartIcon={PlusIcon}
        onClick={() => {
          trackEvent('employer_buy_slot', { email: user?.email });
        }}
        // disabled
      >
        {t('payment-and-invoices:buy_flexible_slots')}
      </V2CustomLink>
      {/* </Tooltip> */}
    </HeaderWrapper>
  );
};

const ActiveSlotExpiringSoonWarning = () => {
  const { idleFlexibleSlotsLength } = useFlexibleSlotActiveIdleLength();
  const { t } = useTranslation('payment-and-invoices');

  return !idleFlexibleSlotsLength ? (
    <Alert
      variant="warning"
      wrapperStyling={[tw`w-full`]}
      text={
        <div tw="flex flex-col justify-start items-start gap-1">
          {[
            t('flexible_slot_expiring'),
            t('buy_more_slots_to_continue'),
            t('service_will_active_for_one_week_at_cost_of_100_euro'),
          ].map((text, i) => (
            <Title key={i} content={text} fontSize="base" fontWeight="medium" textColor="orange-700" />
          ))}
        </div>
      }
    />
  ) : null;
};

const SlotSummary = () => {
  const { activeFlexibleSlotsLength, idleFlexibleSlotsLength, subscriptionSlotsLength } =
    useFlexibleSlotActiveIdleLength();
  const { t } = useTranslation('payment-and-invoices');
  return (
    <div tw="self-stretch justify-start items-start gap-2 inline-flex">
      <Title
        content={`${activeFlexibleSlotsLength + subscriptionSlotsLength} ${t('active')}`}
        fontSize="base"
        fontWeight="semibold"
        textColor="neutral-700"
      />
      <Title
        content={`${idleFlexibleSlotsLength} ${t('inactive')}`}
        fontSize="base"
        fontWeight="semibold"
        textColor="neutral-200"
      />
    </div>
  );
};

const ActionButtons = () => {
  const { showMessages } = useIntercom();
  const { trackEvent } = useAnalytics();
  // const [, setIsOpen] = useAtom(flexibleSlotAddCancelConfirmationModalAtom);
  const setFlexibleSlotConfirmationType = useSetAtom(flexibleSlotConfirmationTypeAtom);
  const { idleFlexibleSlotsLength } = useFlexibleSlotActiveIdleLength();
  const { t } = useTranslation('payment-and-invoices');
  const { user } = useAuth();

  return (
    <div tw="self-stretch justify-center flex-col md:(flex-row justify-end) items-center gap-4 inline-flex">
      {idleFlexibleSlotsLength !== 0 ? (
        <ButtonV2
          twStyle={tw`w-full md:w-fit`}
          onClick={() => {
            trackEvent('employer_activate_slot', { email: user?.email, phone: user?.phone_number });
            // setIsOpen(true); // opens the slot add confirmation modal
            setFlexibleSlotConfirmationType('add');
            showMessages();
          }}
        >
          {t('activate_a_flexible_slot')}
        </ButtonV2>
      ) : null}
      <ButtonV2
        StartIcon={HeadphoneIcon}
        variant="tertiary"
        title={t('contact_support')}
        twStyle={tw`w-full md:w-fit`}
        onClick={() => {
          trackEvent('employer_click_support', {
            email: user?.email,
            phone: user?.phone_number,
            source_page: 'payments-and-invoices',
          });
          showMessages();
        }}
      >
        <Title content={t('contact_support')} fontSize="base" fontWeight="semibold" textColor="neutral-700" />
      </ButtonV2>
    </div>
  );
};

const SlotCards = () => {
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const [, setIsOpen] = useAtom(flexibleSlotAddCancelConfirmationModalAtom);
  const flexibleSlots = useFlexibleSlots();
  const subscriptionSlots = useFlexibleSubscriptionSlots();
  const setSelectedFlexibleSlotsData = useSetAtom(selectedFlexibleSlotsDataAtom);
  const setFlexibleSlotConfirmationType = useSetAtom(flexibleSlotConfirmationTypeAtom);

  const renderSlotCards = React.useCallback(
    (slots: FlexibleSlot[] | IndexStripeSubscriptions, title: string, dateLabel: string) =>
      slots
        ?.map((v: FlexibleSlot | StripeSubscription, i: number) => {
          if (v.status === 'active') {
            const date = v['expires_at']
              ? formatDateToDayMonthYear(v['expires_at'])
              : v['current_period_end']
              ? formatDateToDayMonthYear(fromUnixTime(v['current_period_end']).toISOString())
              : '';
            const isExpirePeriodEnding =
              v['expires_at'] && new Date(v['expires_at']).getTime() - Date.now() < 1000 * 60 * 60 * 24 * 7;

            const toBeCancelledAtPeriodEnd = v?.cancel_at_period_end || !!(v as StripeSubscription)?.cancel_at;
            const slots = (v as StripeSubscription)?.items?.length && (v as StripeSubscription)?.items[0]?.quantity;

            return (
              <li key={`${title}-${date}-${i}`} tw="self-stretch">
                <FlexibleSlotCard
                  status="active"
                  title={title}
                  dateLabel={dateLabel}
                  date={date}
                  cancelAt={(v as StripeSubscription)?.cancel_at}
                  currentPeriodEnd={(v as StripeSubscription)?.current_period_end}
                  orderNumber={slots || 1}
                  onSlotCloseCallback={() => {
                    trackEvent('employer_cancel_slot', { email: user?.email, phone: user?.phone_number });
                    setSelectedFlexibleSlotsData(v);
                    setFlexibleSlotConfirmationType('cancel');
                    setIsOpen(true);
                  }}
                  showCrossIcon={!toBeCancelledAtPeriodEnd}
                  canceledAtPeriodEnd={toBeCancelledAtPeriodEnd}
                  isExpirePeriodEnding={isExpirePeriodEnding} // if flexible slot expires in 7 days
                  isTrial={isFlexibleSlot(v) ? v.is_trial : false}
                />
              </li>
            );
          }
          return null;
        })
        .filter(Boolean),
    [
      setFlexibleSlotConfirmationType,
      setIsOpen,
      setSelectedFlexibleSlotsData,
      trackEvent,
      user?.email,
      user?.phone_number,
    ]
  );
  const { t } = useTranslation('payment-and-invoices');
  return (
    <ul tw="self-stretch flex-col justify-start items-start gap-4 flex">
      {renderSlotCards(subscriptionSlots, t('subscription_slot'), t('next_payment_on'))}
      {renderSlotCards(flexibleSlots, t('flexible_slot'), t('expires_on'))}
    </ul>
  );
};

const IdleSlots = () => {
  const flexibleSlots = useFlexibleSlots();
  const { activeSlots, idleFlexibleSlotsLength } = useFlexibleSlotActiveIdleLength();
  const { t } = useTranslation('payment-and-invoices');

  const renderIdleSlots = React.useMemo(() => {
    const groupedSlots = flexibleSlots?.reduce((acc, v) => {
      if (v.status === 'idle') {
        const date = formatDate(v.expires_at);
        if (!acc[date]) acc[date] = [];
        acc[date].push(v);
      }
      return acc;
    }, {} as Record<string, FlexibleSlot[]>);

    return groupedSlots
      ? Object.entries(groupedSlots).map(([date, slots]) => (
          <li key={date} tw="self-stretch">
            <FlexibleSlotCard
              status="idle"
              title={t('flexible_slot')}
              dateLabel={t('activate_before')}
              orderNumber={slots.length}
              date={date}
              isTrial={slots.some((s) => s.is_trial)}
            />
          </li>
        ))
      : [];
  }, [flexibleSlots, t]);

  return (
    <IdleSlotsWrapper showBorderTop={activeSlots > 0 && idleFlexibleSlotsLength > 0}>
      {renderIdleSlots}
    </IdleSlotsWrapper>
  );
};

export function computeSlotsInfo(flexibleSlots?: FlexibleSlot[], subscriptionSlots?: IndexStripeSubscriptions) {
  const idleFlexibleSlotsLength = flexibleSlots?.filter((v) => v.status === 'idle')?.length || 0;
  const activeFlexibleSlotsLength = flexibleSlots?.filter((v) => v.status === 'active')?.length || 0;
  const subscriptionSlotsLength = subscriptionSlots?.filter((v) => v.status === 'active')?.length || 0;
  const activeSlots = subscriptionSlotsLength + activeFlexibleSlotsLength;
  const noData = !activeFlexibleSlotsLength && !idleFlexibleSlotsLength && !subscriptionSlotsLength;

  return { activeFlexibleSlotsLength, idleFlexibleSlotsLength, subscriptionSlotsLength, noData, activeSlots };
}

export const useFlexibleSlotActiveIdleLength = () => {
  const flexibleSlots = useFlexibleSlots();
  const subscriptionSlots = useFlexibleSubscriptionSlots();
  const { activeFlexibleSlotsLength, idleFlexibleSlotsLength, subscriptionSlotsLength, noData, activeSlots } =
    React.useMemo(() => computeSlotsInfo(flexibleSlots, subscriptionSlots), [flexibleSlots, subscriptionSlots]);

  return { activeFlexibleSlotsLength, idleFlexibleSlotsLength, subscriptionSlotsLength, noData, activeSlots };
};

const HeaderWrapper = styled.div`
  ${tw`self-stretch h-10 justify-between items-center inline-flex`}
`;

const TitleWrapper = styled.div`
  ${tw`justify-start items-center gap-2 flex`}
`;

const ContentWrapper = styled.div`
  ${tw`self-stretch p-5 rounded-2xl border border-neutral-40 flex-col justify-start items-start gap-10 flex overflow-y-auto`}
`;
const IdleSlotsWrapper = styled.ul<{
  showBorderTop?: boolean;
}>`
  ${tw`self-stretch flex-col justify-start items-start gap-4 flex`}
  ${({ showBorderTop }) => showBorderTop && tw`border-t border-neutral-40 pt-4`}
`;
export default FlexibleSlotsPageContent;
